<template>
  <div class="input-range">
    <div class="input-range__wrapper w-full">
      <input
        ref="inputRange"
        class="input-range__input w-full"
        type="range"
        :min="min"
        :max="max"
        :step="step"
        :value="modelValue"
        @input="updateValue($event.target as HTMLInputElement)"
      />
      <div class="input-range__dots">
        <span
          v-for="n in steps"
          :ref="(el) => (dotRefs[n - 1] = el as HTMLElement | null)"
          :key="`range-step-${n}`"
          class="dot"
          :class="{ invisible: n === modelValue }"
        />
      </div>
      <div ref="tooltip" class="input-range__tooltip" :style="{ left: tooltipPosition + 'px' }">
        {{ modelValue }},0
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { nextTick } from 'vue';
const props = defineProps({
  modelValue: {
    type: Number,
    required: true
  },
  min: {
    type: Number,
    default: 0
  },
  max: {
    type: Number,
    default: 5
  },
  step: {
    type: Number,
    default: 1
  }
});

const emits = defineEmits(['update:modelValue']);

const inputRange = ref<HTMLInputElement | null>(null);
const tooltip = ref<HTMLInputElement | null>(null);
const tooltipPosition = ref(0);
const dotRefs = ref<(HTMLElement | null)[]>([]);

const updateValue = (target: HTMLInputElement | null) => {
  if (target) {
    emits('update:modelValue', Number(target.value));
  }
};

const steps = (props.max - props.min) / props.step + 1;

const updateTooltipPosition = () => {
  const range = inputRange.value;
  const tooltipElement = tooltip.value;
  if (range && tooltipElement) {
    const stepIndex = Math.round((props.modelValue - props.min) / props.step);
    const dotElement = dotRefs.value[stepIndex];
    if (dotElement) {
      const dotRect = dotElement.getBoundingClientRect();
      const rangeRect = range.getBoundingClientRect();
      const dotCenter = dotRect.left + dotRect.width / 2 - rangeRect.left;
      tooltipPosition.value = dotCenter;
    }
  }
};

watch(
  () => props.modelValue,
  () => {
    updateTooltipPosition();
  },
  { immediate: true }
);

onMounted(() => {
  nextTick(() => {
    updateTooltipPosition();
  });
});
</script>

<style lang="scss">
input[type='range'] {
  /* removing default appearance */
  -webkit-appearance: none;
  appearance: none;
  /* creating a custom design */
  cursor: pointer;
  outline: none;

  /*  styling the track  */
  height: 8px;
  background: #83ba40;
  border-radius: 32px;

  /* Track: webkit browsers */
  &::-webkit-slider-runnable-track {
    height: 8px;
    background: #83ba40;
    border-radius: 32px;
  }

  /* Track: Mozilla Firefox */
  &::-moz-range-track {
    height: 8px;
    background: #83ba40;
    border-radius: 32px;
  }

  /* Thumb: webkit */
  &::-webkit-slider-thumb {
    /* removing default appearance */
    -webkit-appearance: none;
    appearance: none;
    /* creating a custom design */
    width: 8px;
    height: 48px;
    background-color: #83ba40;
    margin-top: -20px; /* Adjust the position to align with the track */
    border-radius: 6px;
    z-index: 3;

    box-shadow:
      -8px 0 0 0 #222629,
      /* White shadow on the left */ 8px 0 0 0 #222629; /* White shadow on the right */
  }

  /* Thumb: Mozilla Firefox */
  &::-moz-range-thumb {
    /* removing default appearance */
    -webkit-appearance: none;
    appearance: none;
    /* creating a custom design */
    width: 8px;
    height: 48px;
    background-color: #83ba40;
    margin-top: -20px; /* Adjust the position to align with the track */
    border-radius: 6px;
    z-index: 3;

    box-shadow:
      -8px 0 0 0 #1c1f21,
      /* White shadow on the left */ 8px 0 0 0 #1c1f21; /* White shadow on the right */
  }
}

/*@media (max-width: 768px) {
  input[type='range'] {
    &::-webkit-slider-thumb {
      box-shadow:
        -8px 0 0 0 #030307,
        8px 0 0 0 #030307;
    }

    &::-moz-range-thumb {
      box-shadow:
        -8px 0 0 0 #030307,
        8px 0 0 0 #030307;
    }
  }
} */

.input-range {
  position: relative;

  &__wrapper {
    position: relative;
  }

  &__input {
    position: relative;
    width: 100%;
    z-index: 2; /* Ensure the slider is above the dots */
  }

  &__dots {
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    display: flex;
    justify-content: space-between;
    z-index: 2; /* Ensure the dots are below the slider */
    pointer-events: none; /* Allow clicks to pass through */
    padding-left: 4px;
    padding-right: 4px;

    .dot {
      width: 4px;
      height: 4px;
      background-color: #020303;
      border-radius: 50%;
      position: relative;
    }
  }

  /* Tooltip styles */
  &__tooltip {
    position: absolute;
    top: -40px;
    transform: translateX(-50%);
    background-color: transparent;
    color: #9ed355;
    white-space: nowrap;
    z-index: 3; /* Ensure the tooltip is above the slider */
  }
}
</style>
